import {
  CampaignVendors_campaign_agentPhones,
  CampaignVendors_campaign_ivrAudioUrls,
  CampaignVendors_campaign_vendorAccounts,
} from 'types/apollo/CampaignVendors';
import { AgentPhoneStatus } from 'types/apollo/globalTypes';
import { ivrFolderFromUrls } from 'utils/ivr';
import { Vendor, vendorIdToName } from 'utils/vendor';

/** What an unprovisioned campaign is offered, matching the all-Telnyx default. */
export const DEFAULT_TELNYX_VOLUME = 100;

/**
 * The Telnyx number a purchase would short-circuit on, or null if one can still be bought.
 * Mirrors `purchaseAgentPhoneNumber`, which matches on vendor and `isActive` — draining sets only
 * `isDraining`, and a campaign may hold another vendor's number and still need a Telnyx one.
 */
export function findExistingAgentPhone(
  agentPhones: CampaignVendors_campaign_agentPhones[],
): string | null {
  return (
    agentPhones.find(
      (p) => String(p.vendorId) === Vendor.TELNYX && p.status !== AgentPhoneStatus.INACTIVE,
    )?.phone ?? null
  );
}

export interface LabelledAgentPhone {
  phone: string;
  vendor: string;
}

/**
 * Live numbers this card does not provision. Shown as context only: a campaign can hold a legacy
 * Twilio number and still need a Telnyx one bought, and hiding it makes the card look wrong.
 */
export function findOtherVendorPhones(
  agentPhones: CampaignVendors_campaign_agentPhones[],
): LabelledAgentPhone[] {
  return agentPhones
    .filter((p) => String(p.vendorId) !== Vendor.TELNYX && p.status !== AgentPhoneStatus.INACTIVE)
    .map((p) => ({ phone: p.phone, vendor: vendorIdToName(String(p.vendorId)) }));
}

/**
 * The campaign's Telnyx traffic share, or the default when it has no Telnyx account yet.
 * A real 0 must survive: provisioning submits whatever this returns, so defaulting it would
 * silently route all traffic to a campaign deliberately held at zero.
 */
export function findTelnyxVolume(
  vendorAccounts: CampaignVendors_campaign_vendorAccounts[],
): number {
  const telnyx = vendorAccounts.find((va) => String(va.vendorId) === Vendor.TELNYX && !va.deleted);
  return telnyx?.volume ?? DEFAULT_TELNYX_VOLUME;
}

/** The folder a campaign was provisioned with, recovered from whichever audio URL it has. */
export function findProvisionedIvrFolder(
  ivrAudioUrls: CampaignVendors_campaign_ivrAudioUrls | null,
): string | null {
  return (
    ivrFolderFromUrls(ivrAudioUrls?.inbound, ivrAudioUrls?.scheduled, ivrAudioUrls?.unscheduled) ||
    null
  );
}
